import {
	EditOutlined,
	InfoCircleOutlined,
	LockOutlined,
	MinusOutlined,
	MoreOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import { Alert, App, Button, Dropdown, Form, Input, Modal, Space, Tag, Typography } from "antd";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "@/shared/hooks/usePermission";
import { usePermissionLabels } from "../hooks/usePermissionLabels";
import { useCreateRole, useDeleteRole, useUpdateRole } from "../hooks/useRoles";
import {
	hasWildcardPermission,
	isRoleScope,
	type ManagedRole,
	type Permission,
	type RoleScope,
	type UpdateRoleInput,
} from "../types";
import { diffPermissions, permissionKey, slugifyRoleId } from "../utils/roleForm";
import { LockedPermissions, PermissionMatrix } from "./PermissionMatrix";
import { ChangeDot } from "./roleVisuals";
import { ScopeCards } from "./ScopeCards";

const { Title } = Typography;

interface RoleFormValues {
	id: string;
	name: string;
	description: string;
	scope?: RoleScope;
	permissions: Permission[];
}

const EMPTY_VALUES: RoleFormValues = {
	id: "",
	name: "",
	description: "",
	scope: undefined,
	permissions: [],
};

function valuesFromRole(role: ManagedRole): RoleFormValues {
	return {
		id: role.id,
		name: role.name,
		description: role.description ?? "",
		// Eski rolda doira NULL (yoki noma'lum) bo'lsa maydon bo'sh qoladi —
		// administrator uni ONGLI tanlashga majbur, jimgina `self` saqlanmaydi.
		scope: isRoleScope(role.scope) ? role.scope : undefined,
		permissions: role.permissions ?? [],
	};
}

interface RoleEditorProps {
	/** Yo'q bo'lsa — yangi rol yaratish. */
	role?: ManagedRole;
	/** Ro'yxatdagi sariq nuqta uchun. ⚠️ Barqaror (`useCallback`) funksiya bo'lishi shart. */
	onDirtyChange: (dirty: boolean) => void;
	/** Yangi rol qoralamasining nomi — ro'yxatda ko'rinadi. */
	onDraftNameChange?: (name: string) => void;
}

function SectionTitle({ children }: { children: ReactNode }) {
	return <div className="mb-3 text-sm font-semibold leading-[22px]">{children}</div>;
}

/**
 * Rolni yaratish/tahrirlash paneli (spec `2026-09-14-rollar-bitta-ekran-design.md` §4).
 *
 * ⚠️ Chaqiruvchi `key` bilan qayta mount qilishi SHART (`roleId` yoki
 * `"new"`): TanStack Router parametr o'zgarganda komponentni QAYTA MOUNT
 * QILMAYDI, forma esa `initialValues` ni faqat mount'da o'qiydi — usiz ikkinchi
 * bosilgan rol birinchisining qiymatlari va «o'zgarish» holatini meros olardi.
 */
export function RoleEditor({ role, onDirtyChange, onDraftNameChange }: RoleEditorProps) {
	const { t } = useTranslation("roles");
	const navigate = useNavigate();
	const { modal } = App.useApp();
	const { can } = usePermission();
	const { resourceLabel, actionLabel } = usePermissionLabels();
	const createRole = useCreateRole();
	const updateRole = useUpdateRole();
	const deleteRole = useDeleteRole();

	const isNew = role === undefined;
	// `*` rol — ruxsatlar va doira QULFLANGAN (backend 422 bilan rad etadi),
	// faqat nom va tavsif tahrirlanadi.
	const isWildcard = Boolean(role && hasWildcardPermission(role.permissions));
	// `roles:read` bor, `roles:update` yo'q — rol ko'rinadi, lekin o'zgartirilmaydi.
	const readOnly = !(isNew || can("roles", "update"));
	const canDelete = Boolean(role && !role.isSystem && can("roles", "delete"));

	const [form] = Form.useForm<RoleFormValues>();
	const [baseline, setBaseline] = useState<RoleFormValues>(() =>
		role ? valuesFromRole(role) : EMPTY_VALUES
	);
	const [idTouched, setIdTouched] = useState(false);

	/*
	 * Formaning joriy qiymatlari — `onValuesChange` dan. ⚠️ `Form.useWatch`
	 * EMAS: u happy-dom testlarida yangilanmadi (o'lchangan — «Saqlash» o'chiq
	 * qolib, 6 ssenariy yiqildi), ya'ni `dirty` ga tayanadigan har narsa
	 * (tugma, tasma, `useBlocker`) sinovsiz qolardi. ⚠️ `setFieldsValue` /
	 * `setFieldValue` `onValuesChange` ni CHAQIRMAYDI — ular ishlatilgan har
	 * joyda `setCurrent` ham chaqiriladi.
	 */
	const [current, setCurrent] = useState<RoleFormValues>(baseline);
	const { id: watchedId, name, description, scope, permissions } = current;

	const permissionDiff = diffPermissions(baseline.permissions, permissions);
	const changes: { key: string; icon: ReactNode; label: string }[] = isNew
		? []
		: [
				...(name !== baseline.name
					? [{ key: "name", icon: <EditOutlined />, label: t("changes.name") }]
					: []),
				...(description !== baseline.description
					? [{ key: "description", icon: <EditOutlined />, label: t("changes.description") }]
					: []),
				...(scope !== baseline.scope
					? [
							{
								key: "scope",
								icon: <EditOutlined />,
								label: t("changes.scope", {
									from: baseline.scope ? t(`scope.${baseline.scope}`) : "—",
									to: scope ? t(`scope.${scope}`) : "—",
								}),
							},
						]
					: []),
				...permissionDiff.added.map((permission) => ({
					key: `+${permissionKey(permission)}`,
					icon: <PlusOutlined className="text-[#1c5cab] dark:text-[#8fb6ea]" />,
					label: `${resourceLabel(permission.resource)} · ${actionLabel(permission.action)}`,
				})),
				...permissionDiff.removed.map((permission) => ({
					key: `-${permissionKey(permission)}`,
					icon: <MinusOutlined className="text-[#d03b3b]" />,
					label: `${resourceLabel(permission.resource)} · ${actionLabel(permission.action)}`,
				})),
			];

	const dirty = isNew
		? watchedId !== "" ||
			name !== "" ||
			description !== "" ||
			scope !== undefined ||
			permissions.length > 0
		: changes.length > 0;

	useEffect(() => {
		onDirtyChange(dirty);
	}, [dirty, onDirtyChange]);

	useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

	useEffect(() => {
		if (isNew) {
			onDraftNameChange?.(name);
		}
	}, [isNew, name, onDraftNameChange]);

	/*
	 * Saqlanmagan o'zgarish bilan boshqa rolga o'tish, ro'yxatga qaytish yoki
	 * sahifani yopish tasdiq so'raydi. Ro'yxatdagi rol bosilishi ham oddiy
	 * navigatsiya — alohida mexanizm kerak emas.
	 *
	 * ⚠️ `skipBlockRef` — foydalanuvchi ONGLI chiqqan yo'llar (bekor qilish,
	 * yaratilgan rolga o'tish, o'chirish) tasdiqsiz o'tadi.
	 */
	const dirtyRef = useRef(dirty);
	const skipBlockRef = useRef(false);
	useEffect(() => {
		dirtyRef.current = dirty;
	}, [dirty]);

	const blocker = useBlocker({
		shouldBlockFn: () => dirtyRef.current && !skipBlockRef.current,
		enableBeforeUnload: () => dirtyRef.current && !skipBlockRef.current,
		withResolver: true,
	});

	const leaveToList = () => {
		skipBlockRef.current = true;
		navigate({ to: "/roles" });
	};

	const revert = () => {
		form.setFieldsValue(baseline);
		setCurrent(baseline);
	};

	const handleFinish = (values: RoleFormValues) => {
		if (!role) {
			createRole.mutate(
				{
					id: values.id,
					name: values.name,
					description: values.description || undefined,
					scope: values.scope as RoleScope,
					permissions: values.permissions ?? [],
				},
				{
					onSuccess: (created) => {
						skipBlockRef.current = true;
						navigate({ to: "/roles/$roleId", params: { roleId: created.id } });
					},
				}
			);
			return;
		}

		const input: UpdateRoleInput = isWildcard
			? { name: values.name, description: values.description }
			: {
					name: values.name,
					description: values.description,
					scope: values.scope,
					permissions: values.permissions ?? [],
				};

		updateRole.mutate(
			{ id: role.id, input },
			{
				onSuccess: (updated) => {
					const next = valuesFromRole(updated);
					setBaseline(next);
					form.setFieldsValue(next);
					setCurrent(next);
				},
			}
		);
	};

	/*
	 * ⚠️ `Popconfirm` EMAS, `modal.confirm`: menyu bandini bosganda `Dropdown`
	 * yopiladi va uning ichidagi `Popconfirm` ham birga yo'qolardi
	 * (`DepartmentProfileHeader` bilan bir xil sabab).
	 */
	const confirmDelete = () => {
		if (!role) {
			return;
		}
		modal.confirm({
			title: t("list.deleteConfirmTitle"),
			content: t("list.deleteConfirmDescription"),
			okText: t("list.delete"),
			cancelText: t("list.cancel"),
			okButtonProps: { danger: true },
			// Xato xabarini mutatsiyaning o'zi ko'rsatadi (biriktirilgan rol — 409).
			onOk: () =>
				deleteRole
					.mutateAsync(role.id)
					.then(leaveToList)
					.catch(() => undefined),
		});
	};

	const onValuesChange = (changed: Partial<RoleFormValues>, all: Partial<RoleFormValues>) => {
		const next = { ...current, ...all };

		// ID nomdan avtomatik — foydalanuvchi ID ni o'zi tahrirlagunicha.
		if (isNew && !idTouched && changed.name !== undefined) {
			next.id = slugifyRoleId(changed.name);
			form.setFieldValue("id", next.id);
		}

		setCurrent(next);
	};

	const pending = createRole.isPending || updateRole.isPending;

	return (
		<Form
			form={form}
			layout="vertical"
			requiredMark={false}
			initialValues={baseline}
			onFinish={handleFinish}
			onValuesChange={onValuesChange}
			className="min-w-0"
		>
			<div className="flex flex-wrap items-start gap-4 border-b border-[#eeefec] px-6 py-4 dark:border-[#22262b]">
				<div className="flex min-w-0 flex-1 flex-col gap-0.5">
					<div className="flex flex-wrap items-center gap-2">
						{/* `!mt-0` — preflight yo'q, `h4` ning brauzer standart chekkasi
						    sarlavhani teglardan pastga surardi (brauzerda ko'rilgan). */}
						<Title level={4} className="!mt-0 !mb-0">
							{role ? baseline.name : t("form.titleNew")}
						</Title>
						{role?.isSystem ? <Tag className="!m-0">{t("list.systemTag")}</Tag> : null}
						{isWildcard ? (
							<Tag color="gold" className="!m-0">
								{t("list.allPermissions")}
							</Tag>
						) : null}
					</div>
					{role ? (
						<div className="flex items-center gap-1.5 text-xs leading-[18px] text-[#8a929e] dark:text-[#6b7280]">
							{role.isSystem ? <InfoCircleOutlined /> : null}
							<span>{role.isSystem ? `${role.id} · ${t("form.systemNote")}` : role.id}</span>
						</div>
					) : null}
				</div>

				<Space size="small">
					<Button onClick={leaveToList}>{t("form.cancel")}</Button>
					{readOnly ? null : (
						<Button type="primary" htmlType="submit" disabled={!(isNew || dirty)} loading={pending}>
							{isNew ? t("form.create") : t("form.save")}
						</Button>
					)}
					{canDelete ? (
						<Dropdown
							trigger={["click"]}
							menu={{
								items: [
									{ key: "delete", danger: true, label: t("list.delete"), onClick: confirmDelete },
								],
							}}
						>
							<Button icon={<MoreOutlined />} aria-label={t("form.moreActions")} />
						</Dropdown>
					) : null}
				</Space>
			</div>

			{changes.length > 0 ? (
				<div className="mx-6 mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-[#f3dca0] bg-[#fff8e6] px-3 py-2 dark:border-[#5c4a1f] dark:bg-[#2b2415]">
					<span className="inline-flex items-center gap-1.5 font-medium text-[#8a5a00] dark:text-[#e3b75a]">
						<ChangeDot />
						{t("changes.title", { count: changes.length })}
					</span>
					{changes.map((change) => (
						<span
							key={change.key}
							className="inline-flex h-6 items-center gap-1.5 rounded-md border border-[#f3dca0] bg-white px-2 text-xs dark:border-[#5c4a1f] dark:bg-[#181b1f]"
						>
							{change.icon}
							{change.label}
						</span>
					))}
					<Button
						type="link"
						size="small"
						className="!ml-auto !px-1 dark:!text-[#8fb6ea]"
						onClick={revert}
					>
						{t("changes.revert")}
					</Button>
				</div>
			) : null}

			{readOnly ? (
				<Alert type="info" showIcon className="!mx-6 !mt-4" title={t("form.readOnly")} />
			) : null}
			{isWildcard ? (
				<Alert
					type="info"
					showIcon
					icon={<LockOutlined />}
					className="!mx-6 !mt-4"
					title={t("form.wildcardLocked")}
				/>
			) : null}

			<div className="flex flex-col gap-7 p-6">
				<section>
					<SectionTitle>{t("form.basicsTitle")}</SectionTitle>
					<div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
						<Form.Item
							name="name"
							label={t("form.nameLabel")}
							rules={[{ required: true, whitespace: true, message: t("form.nameRequired") }]}
						>
							{/* Chegaralar backend `RoleNameSchema`/`RoleDescriptionSchema` bilan bir xil. */}
							<Input placeholder={t("form.namePlaceholder")} maxLength={100} disabled={readOnly} />
						</Form.Item>

						<Form.Item
							name="id"
							label={t("form.idLabel")}
							extra={isNew ? t("form.idAutoHint") : t("form.idLockedHint")}
							rules={
								isNew
									? [
											{ required: true, message: t("form.idRequired") },
											{ pattern: /^[a-z0-9_-]+$/, message: t("form.idPattern") },
										]
									: []
							}
						>
							{/* ID birlamchi kalit — tahrirlashda o'zgartirib bo'lmaydi. */}
							<Input
								placeholder={t("form.idPlaceholder")}
								maxLength={64}
								disabled={!isNew}
								suffix={isNew ? undefined : <LockOutlined className="text-[#a3a8b0]" />}
								onChange={() => setIdTouched(true)}
							/>
						</Form.Item>

						<Form.Item
							name="description"
							label={t("form.descriptionLabel")}
							className="md:col-span-2"
						>
							<Input.TextArea
								rows={2}
								placeholder={t("form.descriptionPlaceholder")}
								maxLength={500}
								disabled={readOnly}
							/>
						</Form.Item>
					</div>
				</section>

				<Form.Item
					name="scope"
					label={t("form.scopeLabel")}
					extra={t("form.scopeDescription")}
					rules={isWildcard ? [] : [{ required: true, message: t("form.scopeRequired") }]}
					className="!mb-0"
				>
					<ScopeCards disabled={isWildcard || readOnly} />
				</Form.Item>

				{isWildcard ? (
					<LockedPermissions />
				) : (
					<Form.Item name="permissions" className="!mb-0">
						<PermissionMatrix
							baseline={isNew ? undefined : baseline.permissions}
							scope={scope}
							disabled={readOnly}
						/>
					</Form.Item>
				)}
			</div>

			<Modal
				open={blocker.status === "blocked"}
				title={t("leave.title")}
				okText={t("leave.confirm")}
				cancelText={t("leave.stay")}
				okButtonProps={{ danger: true }}
				onOk={() => blocker.proceed?.()}
				onCancel={() => blocker.reset?.()}
			>
				{t("leave.description")}
			</Modal>
		</Form>
	);
}
